// TESTIFY upload signing (§13.8) — CONFIRMED against the live endpoint
// (accepted upload, 2026-07-26). Two HMAC layers, all outputs hex strings:
//
//   signingKey   = HMAC_SHA256(EXAM_SALT, "{examCode}:{lrn}:{testId}")   → 64-hex
//   signature    = HMAC_SHA256(signingKey, canonicalPayload)            → 64-hex
//   fileSignature= HMAC_SHA256(firstItemSigningKey, itemSigs.join(":")) → 64-hex
//
// CRITICAL: the HMAC KEY at each step is the *hex STRING* from the previous step
// (its 64 ASCII chars as UTF-8 bytes) — NOT the 32 bytes it decodes to. EXAM_SALT
// (the /export `signing.key`) is likewise used as its literal UTF-8 string.
//
// canonicalPayload = the subject's responses sorted ASC by quesId, each object
// exactly {"quesId","selectedChoiceId","timeSpentSeconds","wasMarked"} in THAT
// key order, JSON.stringify'd (no spaces, no escaped slashes/unicode — which is
// what JSON.stringify already produces).
//
// Uses Web Crypto (SubtleCrypto): browser + Node ≥ 18.

const enc = new TextEncoder();

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** HMAC-SHA256(keyString_as_utf8, message) → hex string. */
async function hmacHex(keyString: string, message: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(keyString), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return toHex(await crypto.subtle.sign('HMAC', key, enc.encode(message)));
}

/** One response row, in the exact key order the canonical payload requires. */
export interface UploadResponse {
  quesId: number;
  selectedChoiceId: number | null; // null for blank or double-mark
  timeSpentSeconds: number;
  wasMarked: 0 | 1;
}

/** Per-subject signing key: HMAC(EXAM_SALT, "examCode:lrn:testId"). */
export function deriveSigningKey(examSalt: string, examCode: string, lrn: string, testId: number | string): Promise<string> {
  return hmacHex(examSalt, `${examCode}:${lrn}:${testId}`);
}

/** Canonical payload string a subject's signature covers. */
export function canonicalPayload(responses: UploadResponse[]): string {
  const sorted = [...responses].sort((a, b) => a.quesId - b.quesId);
  // Rebuild each object so the key order is fixed regardless of the input's order.
  return JSON.stringify(
    sorted.map((r) => ({ quesId: r.quesId, selectedChoiceId: r.selectedChoiceId, timeSpentSeconds: r.timeSpentSeconds, wasMarked: r.wasMarked })),
  );
}

/** Signature for one subject: the signing key signs the canonical payload. */
export async function signSubject(signingKey: string, responses: UploadResponse[]): Promise<string> {
  return hmacHex(signingKey, canonicalPayload(responses));
}

/** File-level signature over all subject signatures, keyed by the first subject's key. */
export function signFile(firstSigningKey: string, itemSignatures: string[]): Promise<string> {
  return hmacHex(firstSigningKey, itemSignatures.join(':'));
}
